import {LocaleDateTimeFormat, NumberFormatter} from 'platform/locale';

import {Nullish, parseDate} from 'shared/web';

import {AlphaCatalogue} from '../../../api/digitalCertificateApi';
import {VehicleV2ResponseBody} from '../../../api/types/onlineOfferTypes';
import {getAlphaCatalogueLabel} from '../../../utils/getAlphaCatalogueLabel';
import {getHourTranslation, getMinuteTranslation} from '../../../utils/getTimeTranslation';

export type ElectricalParametersType = {
  batteryCapacity: string | Nullish;
  batteryWarranty: string | Nullish;
  electricRange: string | Nullish;
  chargingPowerAc: string | Nullish;
  chargingPowerDc: string | Nullish;
  electricEnginePower: string | Nullish;
  chargingTimeAc: string | Nullish;
  chargingTimeDc: string | Nullish;
  electricEngineConsumption: string | Nullish;
  batteryVoltage: string | Nullish;
};

export type AdditionalElectricalParameters = {
  hasRangeExtender: boolean | Nullish;
  batteryType: string | Nullish;
  chargingConnectorType: string | Nullish;
  numberOfBatteries: number | Nullish;
  batteryOwnershipType: string | Nullish;
};

export type GetElectricalParameters = {
  electricalData: ElectricalParametersType;
  additionalElectricalData: AdditionalElectricalParameters;
  hybridType: string | Nullish;
};

export function getElectricalParameters(
  vehicle: VehicleV2ResponseBody | Nullish,
  alphaCatalogue: AlphaCatalogue | Nullish,
  formatDateTime: (dateTimeFormat: LocaleDateTimeFormat, value: Date) => string,
  formatNumber: NumberFormatter
): GetElectricalParameters | null {
  if (!vehicle || !alphaCatalogue) {
    return null;
  }

  const fuelUnitKwh100km = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'fuel_unit',
    key: 'FUEL_UNIT_KWH100KM',
    alphaCatalogue,
  });

  const hybridType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'hybrid_type',
    key: vehicle.hybridType,
    alphaCatalogue,
  });

  const batteryCapacityKWh = vehicle.batteryCapacityWh ? `${vehicle.batteryCapacityWh} kWh` : null;

  const batteryCapacityAh = vehicle.batteryCapacityAh ? `${vehicle.batteryCapacityAh} Ah` : null;

  const batteryCapacity = [batteryCapacityKWh, batteryCapacityAh].filter(Boolean).join(' / ');

  const batteryWarrantyDate = vehicle.batteryWarrantyUntil
    ? `${formatDateTime('dateShort', parseDate(vehicle.batteryWarrantyUntil))}`
    : null;
  const batteryWarrantyKm = vehicle.batteryMileageLimit
    ? `${formatNumber(vehicle.batteryMileageLimit, 0)} km`
    : null;

  const batteryWarranty = [batteryWarrantyKm, batteryWarrantyDate].filter(Boolean).join(' / ');

  const electricRange = vehicle.electricRange ? `${vehicle.electricRange} km` : null;

  const chargingPowerAc = vehicle.chargingAC ? `${vehicle.chargingAC} kW` : null;
  const chargingPowerDc = vehicle.chargingDC ? `${vehicle.chargingDC} kW` : null;
  const electricEnginePower = vehicle.powerElectricEngine
    ? `${vehicle.powerElectricEngine} kW`
    : null;
  const chargingTimeAc = vehicle.chargingTimeAC
    ? `${vehicle.chargingTimeAC} ${getHourTranslation(vehicle.chargingTimeAC)}`
    : null;
  const chargingTimeDc = vehicle.chargingTimeDC
    ? `${vehicle.chargingTimeDC} ${getMinuteTranslation(vehicle.chargingTimeDC)}`
    : null;
  const electricEngineConsumption = vehicle.powerConsumptionCombined
    ? `${vehicle.powerConsumptionCombined} ${fuelUnitKwh100km}`
    : null;
  const batteryVoltage = vehicle.batteryVoltage ? `${vehicle.batteryVoltage} V` : null;
  const hasRangeExtender = vehicle.hasRangeExtender ?? null;
  const batteryType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'battery_type',
    key: vehicle.batteryType,
    alphaCatalogue,
  });
  const chargingConnectorType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'charging_connector_type',
    key: vehicle.chargingConnectorType,
    alphaCatalogue,
  });
  const numberOfBatteries = vehicle.batteryCount ?? null;
  const batteryOwnershipType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'battery_ownership_type',
    key: vehicle.batteryOwnershipType,
    alphaCatalogue,
  });

  return {
    electricalData: {
      batteryCapacity,
      batteryWarranty,
      electricRange,
      chargingPowerAc,
      chargingPowerDc,
      electricEnginePower,
      chargingTimeAc,
      chargingTimeDc,
      electricEngineConsumption,
      batteryVoltage,
    },
    additionalElectricalData: {
      hasRangeExtender,
      batteryType,
      chargingConnectorType,
      numberOfBatteries,
      batteryOwnershipType,
    },
    hybridType,
  };
}
